import React, { useCallback, useEffect, useState } from 'react';
import { BsArrowRepeat, BsClockHistory, BsGear, BsPencil, BsPlus, BsTrash } from 'react-icons/bs';
import { apiFetch, parseJsonSafe, handleUnauthorized } from '../utils/api';
import ServersPanel from '../components/ServersPanel';

const emptyForm = {
  id: null,
  name: '',
  url: '',
  method: 'GET',
  expected_status: '',
  keyword: '',
  timeout_ms: 15000,
  interval_minutes: 10,
  failure_threshold: 2,
  active: true
};

const STATUS_META = {
  up: { label: 'Arriba', className: 'bg-success' },
  down: { label: 'Caído', className: 'bg-danger' },
  paused: { label: 'Pausado', className: 'bg-secondary' },
  pending: { label: 'Sin comprobar', className: 'bg-light text-dark border' }
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
};

const formatDuration = (seconds) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s < 0) return '—';
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ${mins % 60} min`;
  return `${Math.floor(hours / 24)} d ${hours % 24} h`;
};

const formatPercent = (value) => (value === null || value === undefined ? '—' : `${Number(value).toFixed(2)}%`);

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return <span className={`badge ${meta.className}`}>{meta.label}</span>;
};

const MonitoringPage = () => {
  const token = localStorage.getItem('token');

  const [tab, setTab] = useState('webs');
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkingId, setCheckingId] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({
    emails: '', usingAdminFallback: false, effectiveRecipients: [], accounts: [], accountId: null
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState(null);

  const [historyMonitor, setHistoryMonitor] = useState(null);
  const [history, setHistory] = useState(null);

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchMonitors = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await apiFetch('/api/monitoring', { headers: authHeaders() });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Error obteniendo los monitores');
      setMonitors(Array.isArray(data.items) ? data.items : []);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await apiFetch('/api/monitoring/settings/alerts', { headers: authHeaders() });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (res.ok) setSettings(data);
    } catch (_err) { /* los ajustes no son críticos para pintar el listado */ }
  }, [authHeaders]);

  useEffect(() => {
    fetchMonitors();
    fetchSettings();
  }, [fetchMonitors, fetchSettings]);

  // Refresco en segundo plano: el cron va actualizando los estados por su cuenta.
  useEffect(() => {
    const id = setInterval(() => fetchMonitors({ silent: true }), 60000);
    return () => clearInterval(id);
  }, [fetchMonitors]);

  const openNew = () => { setForm(emptyForm); setModalOpen(true); };

  const openEdit = (monitor) => {
    setForm({
      id: monitor.id,
      name: monitor.name || '',
      url: monitor.url || '',
      method: monitor.method || 'GET',
      expected_status: monitor.expected_status || '',
      keyword: monitor.keyword || '',
      timeout_ms: monitor.timeout_ms || 15000,
      interval_minutes: monitor.interval_minutes || 10,
      failure_threshold: monitor.failure_threshold || 2,
      active: Boolean(monitor.active)
    });
    setModalOpen(true);
  };

  const submitMonitor = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiFetch(form.id ? `/api/monitoring/${form.id}` : '/api/monitoring', {
        method: form.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(form)
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Error guardando el monitor');
      setModalOpen(false);
      await fetchMonitors({ silent: true });
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeMonitor = async (monitor) => {
    if (!window.confirm(`¿Eliminar el monitor "${monitor.name}"? Se borrará también su histórico.`)) return;
    try {
      const res = await apiFetch(`/api/monitoring/${monitor.id}`, { method: 'DELETE', headers: authHeaders() });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((await parseJsonSafe(res)).message || 'Error eliminando');
      await fetchMonitors({ silent: true });
    } catch (err) {
      alert(err.message);
    }
  };

  const togglePause = async (monitor) => {
    try {
      const res = await apiFetch(`/api/monitoring/${monitor.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ...monitor, active: !monitor.active })
      });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((await parseJsonSafe(res)).message || 'Error actualizando');
      await fetchMonitors({ silent: true });
    } catch (err) {
      alert(err.message);
    }
  };

  const checkNow = async (monitor) => {
    setCheckingId(monitor.id);
    try {
      const res = await apiFetch(`/api/monitoring/${monitor.id}/check`, { method: 'POST', headers: authHeaders() });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Error comprobando');
      await fetchMonitors({ silent: true });
      const r = data.result || {};
      alert(r.ok
        ? `${monitor.name} responde correctamente (HTTP ${r.statusCode}, ${r.responseMs} ms).`
        : `${monitor.name} NO responde: ${r.error}`);
    } catch (err) {
      alert(err.message);
    } finally {
      setCheckingId(null);
    }
  };

  const openHistory = async (monitor) => {
    setHistoryMonitor(monitor);
    setHistory(null);
    try {
      const res = await apiFetch(`/api/monitoring/${monitor.id}/history`, { headers: authHeaders() });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Error obteniendo el historial');
      setHistory(data);
    } catch (err) {
      alert(err.message);
      setHistoryMonitor(null);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    setSettingsMsg(null);
    try {
      const res = await apiFetch('/api/monitoring/settings/alerts', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ emails: settings.emails, accountId: settings.accountId ?? '' })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Error guardando');
      setSettings(data);
      setSettingsMsg({ type: 'success', text: 'Destinatarios guardados.' });
    } catch (err) {
      setSettingsMsg({ type: 'danger', text: err.message });
    } finally {
      setSavingSettings(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setSettingsMsg(null);
    try {
      const res = await apiFetch('/api/monitoring/settings/test', { method: 'POST', headers: authHeaders() });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo enviar');
      setSettingsMsg({ type: 'success', text: `Email de prueba enviado a: ${data.recipients.join(', ')}` });
    } catch (err) {
      setSettingsMsg({ type: 'danger', text: err.message });
    } finally {
      setTesting(false);
    }
  };

  const downCount = monitors.filter((m) => m.status === 'down').length;
  const activeCount = monitors.filter((m) => m.active).length;
  const with24h = monitors.filter((m) => m.uptime_24h !== null && m.uptime_24h !== undefined);
  const avgUptime = with24h.length
    ? with24h.reduce((sum, m) => sum + Number(m.uptime_24h), 0) / with24h.length
    : null;

  return (
    <div className="small">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h4 className="mb-0">Monitorización</h4>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => { setSettingsMsg(null); setSettingsOpen(true); }}>
          <BsGear className="me-1" /> Avisos
        </button>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${tab === 'webs' ? 'active' : ''}`} onClick={() => setTab('webs')}>
            Webs
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'servidores' ? 'active' : ''}`} onClick={() => setTab('servidores')}>
            Servidores
          </button>
        </li>
      </ul>

      {/* El canal de aviso es el mismo para webs y servidores, así que el aviso va fuera de las pestañas. */}
      {!settings.emails && (
        <div className="alert alert-warning py-2">
          No hay ninguna dirección configurada para los avisos.
          {settings.effectiveRecipients?.length
            ? ' Mientras tanto, se envían a los administradores.'
            : ' Ahora mismo NO se enviará ningún aviso.'}
          {' '}
          <button className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setSettingsOpen(true)}>Configurar</button>
        </div>
      )}

      {tab === 'servidores' && <ServersPanel />}

      {tab === 'webs' && (
      <>
      <div className="d-flex justify-content-end mb-3">
        <button className="btn btn-primary btn-sm" onClick={openNew}>
          <BsPlus className="me-1" /> Nuevo monitor
        </button>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="row g-2 mb-3">
        <div className="col-6 col-md-3">
          <div className="card shadow-sm h-100"><div className="card-body py-2">
            <div className="text-muted">Monitores activos</div>
            <div className="fs-5 fw-semibold">{activeCount}</div>
          </div></div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card shadow-sm h-100"><div className="card-body py-2">
            <div className="text-muted">Caídos ahora</div>
            <div className={`fs-5 fw-semibold ${downCount ? 'text-danger' : ''}`}>{downCount}</div>
          </div></div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card shadow-sm h-100"><div className="card-body py-2">
            <div className="text-muted">Disponibilidad media 24h</div>
            <div className="fs-5 fw-semibold">{avgUptime === null ? '—' : formatPercent(avgUptime)}</div>
          </div></div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card shadow-sm h-100"><div className="card-body py-2">
            <div className="text-muted">Comprobación</div>
            <div className="fs-5 fw-semibold">Automática</div>
          </div></div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          {loading ? (
            <div className="text-muted py-3">Cargando…</div>
          ) : !monitors.length ? (
            <div className="text-muted py-3">
              Todavía no hay ninguna web o servidor monitorizado. Pulsa «Nuevo monitor» para añadir el primero.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>URL</th>
                    <th>Estado</th>
                    <th>Último check</th>
                    <th className="text-end">Latencia</th>
                    <th className="text-end">24h</th>
                    <th className="text-end">7d</th>
                    <th className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {monitors.map((m) => (
                    <tr key={m.id}>
                      <td className="fw-semibold">{m.name}</td>
                      <td>
                        <a href={m.url} target="_blank" rel="noreferrer" className="text-truncate d-inline-block" style={{ maxWidth: 240 }}>
                          {m.url}
                        </a>
                      </td>
                      <td>
                        <StatusBadge status={m.status} />
                        {m.status === 'down' && m.last_error && (
                          <div className="text-danger" style={{ fontSize: '0.75rem' }}>{m.last_error}</div>
                        )}
                      </td>
                      <td>{formatDateTime(m.last_checked_at)}</td>
                      <td className="text-end">{m.last_response_ms ? `${m.last_response_ms} ms` : '—'}</td>
                      <td className="text-end">{formatPercent(m.uptime_24h)}</td>
                      <td className="text-end">{formatPercent(m.uptime_7d)}</td>
                      <td className="text-end text-nowrap">
                        <button
                          className="btn btn-outline-primary btn-sm me-1"
                          title="Comprobar ahora"
                          disabled={checkingId === m.id}
                          onClick={() => checkNow(m)}
                        >
                          <BsArrowRepeat className={checkingId === m.id ? 'spinner-border spinner-border-sm border-0' : ''} />
                        </button>
                        <button className="btn btn-outline-secondary btn-sm me-1" title="Historial" onClick={() => openHistory(m)}>
                          <BsClockHistory />
                        </button>
                        <button className="btn btn-outline-secondary btn-sm me-1" title="Editar" onClick={() => openEdit(m)}>
                          <BsPencil />
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm me-1"
                          title={m.active ? 'Pausar' : 'Reanudar'}
                          onClick={() => togglePause(m)}
                        >
                          {m.active ? 'Pausar' : 'Reanudar'}
                        </button>
                        <button className="btn btn-outline-danger btn-sm" title="Eliminar" onClick={() => removeMonitor(m)}>
                          <BsTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      </>
      )}

      {modalOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{form.id ? 'Editar monitor' : 'Nuevo monitor'}</h5>
                <button type="button" className="btn-close" onClick={() => setModalOpen(false)} />
              </div>
              <form onSubmit={submitMonitor}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-5">
                      <label className="form-label small">Nombre *</label>
                      <input
                        className="form-control"
                        value={form.name}
                        placeholder="Web de Interconecta"
                        onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="col-md-7">
                      <label className="form-label small">URL *</label>
                      <input
                        className="form-control"
                        value={form.url}
                        placeholder="https://interconecta.es"
                        onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
                        required
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label small">Comprobar cada</label>
                      <select
                        className="form-select"
                        value={form.interval_minutes}
                        onChange={(e) => setForm((p) => ({ ...p, interval_minutes: Number(e.target.value) }))}
                      >
                        <option value={5}>5 minutos</option>
                        <option value={10}>10 minutos</option>
                        <option value={15}>15 minutos</option>
                        <option value={30}>30 minutos</option>
                        <option value={60}>1 hora</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small">Avisar tras</label>
                      <select
                        className="form-select"
                        value={form.failure_threshold}
                        onChange={(e) => setForm((p) => ({ ...p, failure_threshold: Number(e.target.value) }))}
                      >
                        <option value={1}>1 comprobación fallida</option>
                        <option value={2}>2 fallidas seguidas</option>
                        <option value={3}>3 fallidas seguidas</option>
                      </select>
                      <div className="form-text">Con 2 se evitan falsas alarmas por cortes puntuales de red.</div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small">Esperar respuesta hasta</label>
                      <select
                        className="form-select"
                        value={form.timeout_ms}
                        onChange={(e) => setForm((p) => ({ ...p, timeout_ms: Number(e.target.value) }))}
                      >
                        <option value={5000}>5 segundos</option>
                        <option value={10000}>10 segundos</option>
                        <option value={15000}>15 segundos</option>
                        <option value={30000}>30 segundos</option>
                      </select>
                    </div>

                    <div className="col-12"><hr className="my-1" /><span className="text-muted">Opciones avanzadas</span></div>

                    <div className="col-md-4">
                      <label className="form-label small">Método</label>
                      <select
                        className="form-select"
                        value={form.method}
                        onChange={(e) => setForm((p) => ({ ...p, method: e.target.value }))}
                      >
                        <option value="GET">GET</option>
                        <option value="HEAD">HEAD (más ligero)</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small">Código HTTP esperado</label>
                      <input
                        className="form-control"
                        type="number"
                        placeholder="Cualquiera 2xx/3xx"
                        value={form.expected_status}
                        onChange={(e) => setForm((p) => ({ ...p, expected_status: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-5">
                      <label className="form-label small">Texto que debe aparecer</label>
                      <input
                        className="form-control"
                        placeholder="Opcional"
                        value={form.keyword}
                        onChange={(e) => setForm((p) => ({ ...p, keyword: e.target.value }))}
                      />
                      <div className="form-text">Detecta webs que responden 200 pero muestran un error.</div>
                    </div>

                    <div className="col-12">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="monitor-active"
                          checked={form.active}
                          onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                        />
                        <label className="form-check-label" htmlFor="monitor-active">
                          Activo (se comprueba automáticamente)
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
                  <button className="btn btn-primary" type="submit" disabled={saving}>
                    {saving ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {settingsOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Avisos de caída</h5>
                <button type="button" className="btn-close" onClick={() => setSettingsOpen(false)} />
              </div>
              <div className="modal-body">
                <label className="form-label small">Enviar desde</label>
                <select
                  className="form-select"
                  value={settings.accountId ?? ''}
                  onChange={(e) => setSettings((p) => ({
                    ...p, accountId: e.target.value === '' ? null : Number(e.target.value)
                  }))}
                >
                  <option value="">Cuenta por defecto (la primera activa)</option>
                  {(settings.accounts || []).map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name ? `${a.name} · ${a.label}` : a.label}
                    </option>
                  ))}
                </select>
                <div className="form-text">
                  Buzón desde el que salen los avisos. Se configuran en Configuración → Cuentas de email.
                </div>

                <label className="form-label small mt-3">Emails que reciben los avisos</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="soporte@interconecta.es, tecnico@interconecta.es"
                  value={settings.emails || ''}
                  onChange={(e) => setSettings((p) => ({ ...p, emails: e.target.value }))}
                />
                <div className="form-text">
                  Separados por comas. Reciben un email cuando una web se cae y otro cuando se recupera.
                  Si lo dejas vacío, los avisos van a los administradores.
                </div>
                {settingsMsg && (
                  <div className={`alert alert-${settingsMsg.type} py-2 mt-3 mb-0`}>{settingsMsg.text}</div>
                )}
              </div>
              <div className="modal-footer justify-content-between">
                <button className="btn btn-outline-secondary" onClick={sendTest} disabled={testing}>
                  {testing ? 'Enviando…' : 'Enviar email de prueba'}
                </button>
                <div className="d-flex gap-2">
                  <button className="btn btn-outline-secondary" onClick={() => setSettingsOpen(false)}>Cerrar</button>
                  <button className="btn btn-primary" onClick={saveSettings} disabled={savingSettings}>
                    {savingSettings ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {historyMonitor && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Historial · {historyMonitor.name}</h5>
                <button type="button" className="btn-close" onClick={() => setHistoryMonitor(null)} />
              </div>
              <div className="modal-body">
                {!history ? (
                  <div className="text-muted">Cargando…</div>
                ) : (
                  <>
                    <h6>Caídas registradas</h6>
                    {!history.incidents.length ? (
                      <p className="text-muted">Ninguna caída registrada. </p>
                    ) : (
                      <table className="table table-sm">
                        <thead>
                          <tr><th>Inicio</th><th>Fin</th><th>Duración</th><th>Causa</th></tr>
                        </thead>
                        <tbody>
                          {history.incidents.map((inc) => (
                            <tr key={inc.id}>
                              <td>{formatDateTime(inc.started_at)}</td>
                              <td>{inc.resolved_at ? formatDateTime(inc.resolved_at) : <span className="badge bg-danger">En curso</span>}</td>
                              <td>{formatDuration(inc.duration_seconds)}</td>
                              <td className="text-muted">{inc.cause || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    <h6 className="mt-4">Últimas comprobaciones</h6>
                    <table className="table table-sm table-striped">
                      <thead>
                        <tr><th>Fecha</th><th>Resultado</th><th className="text-end">HTTP</th><th className="text-end">Latencia</th><th>Detalle</th></tr>
                      </thead>
                      <tbody>
                        {history.checks.map((c) => (
                          <tr key={c.id}>
                            <td>{formatDateTime(c.checked_at)}</td>
                            <td>
                              <span className={`badge ${c.ok ? 'bg-success' : 'bg-danger'}`}>{c.ok ? 'OK' : 'Fallo'}</span>
                            </td>
                            <td className="text-end">{c.status_code || '—'}</td>
                            <td className="text-end">{c.response_ms ? `${c.response_ms} ms` : '—'}</td>
                            <td className="text-muted">{c.error || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setHistoryMonitor(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitoringPage;
